//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import {
  IStateParamsService,
  IStateService,
  IUrlRouterProvider,
} from 'angular-ui-router';

import {
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

// TODO: PD-229241 - Remove feature flag usage once Harmony AI Creators Integration is fully released
// Remove `hasFeature(FeatureNames.HarmonyAiCreatorsIntegration)` throughout this file.
// These needed to be defined as factories to avoid a race condition

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  const isHarmonyAiCreatorsIntegrationFeatureEnabled = hasFeature(
    FeatureNames.HarmonyAiCreatorsIntegration,
  );

  urlRouterProvider.when(
    '/influencers/user/{username}?{list}&{from}&{network}',
    '/influencers/user/{username}/media?{list}&{from}&{network}',
  );

  if (isHarmonyAiCreatorsIntegrationFeatureEnabled) {
    urlRouterProvider.when('/influencers', '/influencers/harmony-ai');
  } else {
    urlRouterProvider.when('/influencers', '/influencers/search');
    urlRouterProvider.when('/influencers/harmony-ai', '/influencers/search');
    urlRouterProvider.when(
      '/influencers/user/{username}/insights?{list}&{from}&{network}',
      '/influencers/user/{username}/media?{list}&{from}&{network}',
    );
    urlRouterProvider.when(
      '/influencers/user/{username}/similar-creators?{list}&{from}&{network}',
      '/influencers/user/{username}/media?{list}&{from}&{network}',
    );
  }
}

export const states = {
  influencers: StateFactory({
    url: '/influencers',
    template: componentNameToTemplate('influencersPage'),
    data: {
      title: 'Creators',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () => hasFeature(FeatureNames.InfluencersDiscovery),
    },
    redirectTo: () =>
      hasFeature(FeatureNames.HarmonyAiCreatorsIntegration)
        ? 'influencers.harmony-ai'
        : 'influencers.search',
    resolve: {
      ...lazyLoadModule(
        'curalate.influencers',
        () => import('./InfluencersApp'),
      ),
    },
  }),

  'influencers.search': StateFactory({
    url: '/search',
    views: {
      main: {
        template: componentNameToTemplate('influencersFind'),
      },
    },
    data: {
      title: 'Search Creators by Username',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () => hasFeature(FeatureNames.InfluencersDiscovery),
    },
  }),

  'influencers.harmony-ai': StateFactory({
    url: '/harmony-ai?:q&:fid&:fd&:lookalike',
    views: {
      main: {
        template: componentNameToTemplate('harmonyAi'),
      },
    },
    data: {
      title: 'AI Creator Search',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () => hasFeature(FeatureNames.HarmonyAiCreatorsIntegration),
    },
  }),

  'influencers.listless': StateFactory({
    url: '/list',
    views: {
      main: {
        template: `<influencers-no-lists-page
            class="cu-w-100 flex-tall cu-h-100"
          ></influencers-no-lists-page>`,
      },
    },
    data: {
      title: 'Creators No Lists Page',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () => hasFeature(FeatureNames.InfluencersDiscovery),
    },
  }),

  'influencers.list': StateFactory({
    url: '/list/:listId',
    params: { listId: null },
    views: {
      main: {
        template: `<influencers-list-page
            list-id="::listId"
            class="cu-w-100 flex-tall cu-h-100"
          ></influencers-list-page>`,
        controller: [
          '$scope',
          '$state',
          '$stateParams',
          (
            $scope: Record<string, unknown>,
            $state: IStateService,
            $stateParams: IStateParamsService,
          ) => {
            $scope.listId = $stateParams.listId;
          },
        ],
      },
    },
    data: {
      title: 'Creator Lists',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () => hasFeature(FeatureNames.InfluencersDiscovery),
    },
  }),

  'influencers.user': StateFactory({
    url: '/user/:username?:list&:from&:network&:campaignSlugName',
    views: {
      main: {
        template: componentNameToTemplate('influencerDetailsPage'),
      },
    },
    data: {
      title: 'Creator Details',
      navGroup: NavGroup.INFLUENCERS,
    },
  }),
  'influencers.user-media': StateFactory({
    url: '/user/:username/media?:list&:from&:network&:campaignSlugName',
    views: {
      main: {
        template: componentNameToTemplate('influencerDetailsPage'),
      },
    },
    data: {
      title: 'Creator Details - Media',
      navGroup: NavGroup.INFLUENCERS,
    },
  }),
  'influencers.user-insights': StateFactory({
    url: '/user/:username/insights?:list&:from&:network&:campaignSlugName',
    views: {
      main: {
        template: componentNameToTemplate('influencerDetailsPage'),
      },
    },
    data: {
      title: 'Creator Details - Insights',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.HarmonyAiCreatorsIntegration),
    },
  }),
  'influencers.user-similar-creators': StateFactory({
    url: '/user/:username/similar-creators?:list&:from&:network&:campaignSlugName',
    views: {
      main: {
        template: componentNameToTemplate('influencerDetailsPage'),
      },
    },
    data: {
      title: 'Creator Details - Similar Creators',
      navGroup: NavGroup.INFLUENCERS,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.HarmonyAiCreatorsIntegration),
    },
  }),
  'influencers.user-profile': StateFactory({
    url: '/user/:username/profile?:list&:from&:network&:campaignSlugName',
    views: {
      main: {
        template: componentNameToTemplate('influencerDetailsPage'),
      },
    },
    data: {
      title: 'Creator Details - Profile',
      navGroup: NavGroup.INFLUENCERS,
    },
  }),
};
